import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FkIcon, type FkIconName } from "./Icons";
import { useDismiss } from "./useDismiss";
import {
  EMPTY_NOTIFICATIONS,
  notificationTime,
  useNotifications,
  type NotificationsData,
  type ShellNotification,
} from "./useNotifications";

/** Вид события: значок, цвет и подпись фильтра на странице «Уведомления». */
export interface NotificationKindMeta {
  icon: FkIconName;
  tone: "warning" | "error" | "success" | "neutral";
  label: string;
}

export const NOTIFICATION_KINDS: Record<string, NotificationKindMeta> = {
  promo_codes_waiting: { icon: "gift", tone: "warning", label: "Промокоды" },
  campaign_stopped: { icon: "pause", tone: "error", label: "Кампании" },
  // Лимит расходов кампании исчерпан (пакет 7, п.37): ссылка открывает окно «Увеличить бюджет».
  budget_exhausted: { icon: "wallet", tone: "warning", label: "Бюджет кампаний" },
  // Пакет 7b, п.23: согласование кампаний партнёров — УК ждёт очередь, партнёру вернули кампанию.
  approvals_pending: { icon: "check", tone: "warning", label: "Согласование" },
  approval_returned: { icon: "undo", tone: "warning", label: "Согласование" },
  low_balance: { icon: "wallet", tone: "error", label: "Баланс" },
  topup_paid: { icon: "check", tone: "success", label: "Пополнения" },
  provider_balance_low: {
    icon: "alert",
    tone: "error",
    label: "Баланс SMS-центра",
  },
  // Пакет 2 «50 улучшений»: прогноз баланса SMSC, тишина канала, медленные отчёты.
  provider_forecast: { icon: "wallet", tone: "warning", label: "Запас SMS-центра" },
  channel_silent: { icon: "pause", tone: "error", label: "Каналы" },
  report_slow: { icon: "clock", tone: "warning", label: "Отчёты" },
};

/** Неизвестный вид из нового Go — нейтральный значок и «Другое». */
export function notificationKind(kind: string): NotificationKindMeta {
  return (
    NOTIFICATION_KINDS[kind] ?? { icon: "bell", tone: "neutral", label: "Другое" }
  );
}

/** Счётчик на колокольчике: больше 9 — «9+». */
export function bellBadge(count: number): string | null {
  if (!Number.isFinite(count) || count <= 0) return null;
  return count > 9 ? "9+" : String(Math.floor(count));
}

function Row({
  item,
  onPick,
}: {
  item: ShellNotification;
  onPick: () => void;
}) {
  const kind = notificationKind(item.kind);
  const time = notificationTime(item.at);
  const body = (
    <>
      <span className={`fk-bell-icon fk-bell-${kind.tone}`}>
        <FkIcon name={kind.icon} size={15} />
      </span>
      <span className="fk-bell-body">
        <span className="fk-bell-title">{item.title}</span>
        {item.text && <span className="fk-bell-text">{item.text}</span>}
        {time && <span className="fk-bell-time">{time}</span>}
      </span>
    </>
  );
  return (
    <li>
      {item.link ? (
        <Link className="fk-bell-row" to={item.link} onClick={onPick}>
          {body}
        </Link>
      ) : (
        <div className="fk-bell-row">{body}</div>
      )}
    </li>
  );
}

/**
 * Колокольчик шапки (вид). Данных нет, ошибка или старый сервер — пустой список
 * «Новых событий нет» без счётчика. `defaultOpen` — для тестов разметки.
 */
export function NotificationsBell({
  data,
  defaultOpen = false,
}: {
  data: NotificationsData | undefined;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const ref = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, ref, close);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);

  const safe = data ?? EMPTY_NOTIFICATIONS;
  const badge = bellBadge(safe.count);
  const label = badge
    ? `Уведомления: требуют действия — ${safe.count}`
    : safe.items.length
      ? "Уведомления: событий, требующих действия, нет"
      : "Уведомления: новых событий нет";
  return (
    <div className="fk-pop-anchor" ref={ref}>
      <button
        type="button"
        className={`fk-icon-btn fk-bell${open ? " is-open" : ""}`}
        title="Уведомления"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="fk-bell-panel"
        onClick={() => setOpen((value) => !value)}
      >
        <FkIcon name="bell" size={16} />
        {badge && (
          <span className="fk-bell-badge" aria-hidden="true">
            {badge}
          </span>
        )}
      </button>
      {open && (
        <div
          id="fk-bell-panel"
          className="fk-pop fk-bell-pop"
          role="dialog"
          aria-label="Уведомления"
        >
          <div className="fk-pop-head">
            <span>Уведомления</span>
            {badge && (
              <span className="fk-pop-count" title="Требуют действия">
                {safe.count}
              </span>
            )}
          </div>
          {safe.items.length ? (
            <ul className="fk-bell-list">
              {safe.items.map((item) => (
                <Row key={item.id} item={item} onPick={close} />
              ))}
            </ul>
          ) : (
            <div className="fk-bell-empty">
              <FkIcon name="check" size={18} />
              <span>Новых событий нет</span>
            </div>
          )}
          <div className="fk-pop-foot">
            События SMS-центра: промокоды, остановки кампаний, баланс и
            пополнения. Пропадают сами, когда причина устранена; пополнения —
            через 7 дней. Число на колокольчике — только то, что требует
            действия.{" "}
            <Link className="fk-bell-all" to="/notifications" onClick={close}>
              Все уведомления →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

/** Колокольчик с данными GET /api/notifications. */
export function HeaderBell() {
  const query = useNotifications();
  return <NotificationsBell data={query.data} />;
}
